import {
  addDoc,
  collection,
  deleteDoc,
  setDoc,
  doc,
  getDoc,
  query,
  onSnapshot,
  QueryFieldFilterConstraint,
  QueryOrderByConstraint,
  orderBy,
  limit,
  getDocs,
  QueryStartAtConstraint,
  QueryLimitConstraint,
} from 'firebase/firestore'

import { FirebaseCollection } from '@/constants/firebase-collection'
import { firestore } from '@/services/firebase'
import { delay } from '@/utils/tricks'

interface GetOneProps {
  companyId: string
  clientCreditId: string
}
export const getOne = async ({
  companyId,
  clientCreditId,
}: GetOneProps): Promise<ClientCredit | undefined> => {
  const docRef = doc(
    firestore,
    FirebaseCollection.BASE_COMPANY,
    companyId,
    FirebaseCollection.BASE_CLIENT_CREDIT,
    clientCreditId,
  )
  const docSnap = await getDoc(docRef)

  try {
    if (docSnap.exists()) {
      return {
        id: clientCreditId,
        ...docSnap.data(),
      } as ClientCredit
    }
  } catch (error) {
    return undefined
  }

  return undefined
}

interface GetLastIdIncrementProps {
  companyId: string
}
export const getLastIdIncrement = async ({
  companyId,
}: GetLastIdIncrementProps) => {
  const q = query(
    collection(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_CLIENT_CREDIT,
    ),
    orderBy('idIncrement', 'desc'),
    limit(1),
  )

  const querySnapShot = await getDocs(q)

  if (querySnapShot.size === 0) return undefined

  const orderList: ClientCredit[] = []
  querySnapShot.forEach(doc => {
    orderList.push(doc.data() as ClientCredit)
  })

  return orderList[0]
}

interface GetListProps {
  companyId: string
  setClientCreditList: (creditList: ClientCredit[] | undefined) => void
  setLoadingList: React.Dispatch<React.SetStateAction<boolean>>
  whereList?: QueryFieldFilterConstraint[]
  orderByList?: QueryOrderByConstraint[]
  startAfter?: QueryStartAtConstraint[]
  limit?: QueryLimitConstraint[]
  timeDelay?: number
}
export const getList = async ({
  companyId,
  setClientCreditList,
  setLoadingList,
  whereList,
  orderByList,
  startAfter,
  limit,
  timeDelay,
}: GetListProps) => {
  setLoadingList(true)

  if (timeDelay) await delay(timeDelay)

  const q = query(
    collection(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_CLIENT_CREDIT,
    ),
    ...(whereList || []),
    ...(orderByList || []),
    ...(startAfter || []),
    ...(limit || []),
  )

  onSnapshot(
    q,
    querySnapshot => {
      const clientList: ClientCredit[] = []
      querySnapshot.forEach(doc => {
        const dataItem = { id: doc.id, ...doc.data() } as ClientCredit
        clientList.push(dataItem)
      })
      setLoadingList(false)
      setClientCreditList(clientList)
    },
    () => {
      console.log('client credit - getList() - error')
      setLoadingList(false)
    },
  )
}

interface SaveProps {
  companyId: string
  values: Partial<ClientCredit>
}
export const save = async ({ companyId, values }: SaveProps) =>
  await addDoc(
    collection(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_CLIENT_CREDIT,
    ),
    {
      ...values,
      createdAt: new Date().getTime(),
      dateCreatedAt: new Date().setHours(0, 0, 0, 0),
      updatedAt: new Date().getTime(),
    },
  ).then(resp => resp.id)

interface updateProps {
  companyId: string
  clientCreditId: string
  values: Partial<ClientCredit>
}
export const update = async ({
  companyId,
  clientCreditId,
  values,
}: updateProps) => {
  await setDoc(
    doc(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_CLIENT_CREDIT,
      clientCreditId,
    ),
    {
      ...values,
      updatedAt: new Date().getTime(),
    },
    { merge: true },
  )
}

interface RemoveProps {
  companyId: string
  clientCreditId: string
}
export const remove = async ({ companyId, clientCreditId }: RemoveProps) => {
  await deleteDoc(
    doc(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_CLIENT_CREDIT,
      clientCreditId,
    ),
  )
}
